import { useState } from "react";
import { motion } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import { hydrateSession } from "../lib/authSession";

/**
 * BD1 — password entry for a returning account that HAS a password
 * (check-email said {exists:true, has_password:true}).
 *
 * POSTs /api/auth/login. Server discipline: generic 401 on wrong creds
 * (no enumeration), 429 after repeated failures, 403 password_not_set if a
 * stale client lands here for a passwordless account (we bounce to OTP).
 * Both escape hatches ("one-time code" / "forgot password") route to the
 * existing OTP path — an OTP session may set a new password without the
 * current one (inbox ownership re-proved), which IS the reset flow.
 */
interface PasswordScreenProps {
  email: string;
  /** Login succeeded — session hydrated; parent routes to /chat. */
  onAuthed: (result: { isNew: boolean }) => void;
  /** Fall back to the OTP flow. reason="forgot" → parent offers a password
   * reset step right after the code is verified. */
  onUseOtp: (reason: "alt" | "forgot") => void;
  onBack: () => void;
}

export function PasswordScreen({ email, onAuthed, onUseOtp, onBack }: PasswordScreenProps) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 403 && data?.error === "password_not_set") {
        // Stale branch (password was never set) — hand over to OTP.
        onUseOtp("alt");
        return;
      }
      if (!r.ok || !data?.token || !data?.user) {
        setError(
          (data && (data.message || data.error)) ||
            "Could not sign you in. Please try again.",
        );
        setLoading(false);
        return;
      }
      hydrateSession(data);
      setLoading(false);
      onAuthed({ isNew: Boolean(data.user.is_new) });
    } catch {
      setError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Sign in"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      formTitle="Welcome back."
      showcase={<PhoneShowcase />}
    >
      <motion.form
        onSubmit={handleSubmit}
        noValidate
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
        className="bsure-ui flex flex-col gap-5"
      >
        <p className="bsure-prose text-[13.5px] leading-[1.55] text-[var(--color-ink-60)]">
          Signing in as{" "}
          <span className="text-[var(--color-ink)] font-medium">{email}</span>
        </p>

        {error ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-900"
          >
            {error}
          </div>
        ) : null}

        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          autoFocus
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={loading}
          disabled={!password}
        >
          Sign in
        </Button>

        <div className="flex items-center justify-between text-[12.5px]">
          <button
            type="button"
            onClick={() => onUseOtp("alt")}
            className="text-[var(--color-brand-deep)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors font-medium"
          >
            Use a one-time code instead
          </button>
          <button
            type="button"
            onClick={() => onUseOtp("forgot")}
            className="text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors"
          >
            Forgot password?
          </button>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="bsure-ui mt-1 text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors self-start"
        >
          ← Use a different email
        </button>
      </motion.form>
    </AuthShell>
  );
}

export default PasswordScreen;
